<template>
  <a
    v-if="catalogNumber && supportsMade"
    :href="aiStudioUrl"
    :class="buttonClass"
    @click="handleClick"
  >
    <UseSvg :class="iconClass" icon="stars-corner" />
    {{ buttonText }}
  </a>
</template>

<script>
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";

export default {
  name: "AiStudioButton",
  components: {
    UseSvg
  },
  props: {
    catalogNumber: {
      type: String,
      required: true
    },
    variant: {
      type: String,
      default: "default", // "default", "compact"
      validator: value => ["default", "compact"].includes(value)
    },
    buttonText: {
      type: String,
      default: "Design with AI"
    },
    disabled: {
      type: Boolean,
      default: false
    },
    supportsMade: {
      type: Boolean,
      default: true
    },
    className: {
      type: String,
      default: ""
    },
    side: {
      type: String,
      default: ""
    }
  },
  computed: {
    buttonClass() {
      const baseClass = "ai-studio-button";
      const variantClass = `ai-studio-button--${this.variant}`;
      const disabledClass = this.disabled || !this.supportsMade ? "ai-studio-button--disabled" : "";

      return [baseClass, variantClass, disabledClass, this.className].filter(Boolean).join(" ");
    },
    iconClass() {
      return "ai-studio-button__icon";
    },
    aiStudioUrl() {
      const baseUrl = `/weaver-ai-studio/${this.catalogNumber}`;
      const url = new URL(baseUrl, window.location.origin);

      const currentParams = new URLSearchParams(window.location.search);

      ["made", "page", "v", "ac", "sample"].forEach(param => {
        if (currentParams.has(param)) {
          url.searchParams.set(param, currentParams.get(param));
        }
      });

      if (this.side) {
        url.searchParams.set("side", this.side);
      }

      return url.pathname + url.search;
    }
  },
  methods: {
    handleClick(event) {
      if (this.disabled || !this.supportsMade) {
        event.preventDefault();
        return;
      }

      const mixpanelEventData = { source: "pdp", timestamp: Date.now() };
      if (this.side) mixpanelEventData.placement = this.side;

      submitDtfEvent("ai_studio_cta_clicked", mixpanelEventData);

      this.$emit("click", event);
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/print-settings";
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.ai-studio-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  max-width: 216px;
  height: 40px;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  color: $white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  line-height: 1px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.25);
  border: none;
  outline: none;
  cursor: pointer;
  opacity: 1;
  transform: rotate(0deg);
  font-family: $font-family-secondary;
  box-sizing: border-box;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  &::before,
  &::after {
    border: none;
    outline: none;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.35);
    color: $white;
    text-decoration: none;
    background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
    outline: none;
    border: none;
  }

  &:active {
    transform: translateY(0);
    outline: none;
    border: none;
  }

  &:focus {
    outline: none;
    border: none;
    box-shadow: 0 2px 8px rgba(139, 92, 246, 0.25);
  }

  &:focus-visible {
    outline: none;
    border: none;
  }

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed;
  }

  &--compact {
    font-size: 12px;
    height: 40px; // Keep consistent height even in compact mode

    @include with-xl-mobile {
      font-size: 13px;
    }
  }

  &__icon {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
  }

  // Mobile-specific styling
  @media (max-width: 768px) {
    height: 44px;
    font-weight: 700;
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(139, 92, 246, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(139, 92, 246, 0.4);
    }
  }

  // Small mobile devices
  @media (max-width: 480px) {
    height: 48px;
    border-radius: 14px;

    &__icon {
      width: 20px;
      height: 20px;
    }
  }

  // Mobile pop-up specific styling (for modal contexts)
  .modal &,
  .popup & {
    background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
    box-shadow: 0 4px 20px rgba(139, 92, 246, 0.4);
    border-radius: 12px;
    height: 48px;
    font-weight: 700;

    &:hover {
      background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
      box-shadow: 0 6px 24px rgba(139, 92, 246, 0.5);
    }

    &__icon {
      width: 20px;
      height: 20px;
    }
  }
}
</style>
